'use client';

import { useEffect, useRef, useState } from 'react';

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    const words = paragraph.split(' ');
    let current = '';
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    lines.push(current);
  }
  return lines;
}

export default function BrowserTweetGenerator() {
  const [name, setName] = useState('Ada Lovelace');
  const [handle, setHandle] = useState('ada');
  const [text, setText] = useState(
    'Just shipped a tool that runs entirely in your browser. No servers, no tracking.',
  );
  const [verified, setVerified] = useState(true);
  const [dark, setDark] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const scale = 2;
    const width = 600;
    const padding = 24;
    const avatar = 48;

    ctx.font = `400 22px system-ui, sans-serif`;
    const lines = wrapText(ctx, text, width - padding * 2);
    const lineHeight = 30;
    const height = padding + avatar + 16 + lines.length * lineHeight + 40 + padding;

    canvas.width = width * scale;
    canvas.height = height * scale;
    ctx.scale(scale, scale);

    // card background
    ctx.fillStyle = dark ? '#15202b' : '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // avatar
    ctx.beginPath();
    ctx.arc(padding + avatar / 2, padding + avatar / 2, avatar / 2, 0, Math.PI * 2);
    ctx.fillStyle = '#6366f1';
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = '600 22px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      (name.trim()[0] || '?').toUpperCase(),
      padding + avatar / 2,
      padding + avatar / 2 + 1,
    );
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    // name + handle
    const textX = padding + avatar + 12;
    ctx.fillStyle = dark ? '#ffffff' : '#0f1419';
    ctx.font = '700 17px system-ui, sans-serif';
    const displayName = name.trim() || 'Name';
    ctx.fillText(displayName, textX, padding + 20);
    if (verified) {
      const badgeX = textX + ctx.measureText(displayName).width + 6;
      ctx.fillStyle = '#1d9bf0';
      ctx.beginPath();
      ctx.arc(badgeX + 8, padding + 14, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(badgeX + 4, padding + 14);
      ctx.lineTo(badgeX + 7, padding + 17);
      ctx.lineTo(badgeX + 12, padding + 11);
      ctx.stroke();
    }
    ctx.fillStyle = dark ? '#8b98a5' : '#536471';
    ctx.font = '400 15px system-ui, sans-serif';
    ctx.fillText(`@${handle.trim() || 'handle'}`, textX, padding + 40);

    // tweet text
    ctx.fillStyle = dark ? '#e7e9ea' : '#0f1419';
    ctx.font = '400 22px system-ui, sans-serif';
    let y = padding + avatar + 34;
    for (const line of lines) {
      ctx.fillText(line, padding, y);
      y += lineHeight;
    }

    // footer
    ctx.fillStyle = dark ? '#8b98a5' : '#536471';
    ctx.font = '400 14px system-ui, sans-serif';
    ctx.fillText('cleanor.app', padding, y + 22);

    canvas.toBlob((blob) => {
      if (blob) setDownloadUrl(URL.createObjectURL(blob));
    }, 'image/png');
  }, [name, handle, text, verified, dark]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Tweet</span>
        <h2 className="tool-live-title">Fake tweet generator</h2>
        <p className="tool-live-subtitle">
          Design a mock tweet and download it as an image. Everything is rendered in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Display name</span>
              <input onChange={(e) => setName(e.target.value)} type="text" value={name} />
            </label>
            <label className="tool-live-field">
              <span>Handle</span>
              <input onChange={(e) => setHandle(e.target.value)} type="text" value={handle} />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Tweet text</span>
              <textarea onChange={(e) => setText(e.target.value)} rows={3} value={text} />
            </label>
            <label className="tool-live-field">
              <span>Verified</span>
              <input
                checked={verified}
                onChange={(e) => setVerified(e.target.checked)}
                type="checkbox"
              />
            </label>
            <label className="tool-live-field">
              <span>Dark mode</span>
              <input checked={dark} onChange={(e) => setDark(e.target.checked)} type="checkbox" />
            </label>
          </div>
          <div className="tool-live-actions">
            {downloadUrl ? (
              <a className="button button-secondary" download="fake-tweet.png" href={downloadUrl}>
                Download PNG
              </a>
            ) : null}
          </div>
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
          <p className="tool-live-panel-note">
            Rendered locally with an HTML canvas. This is a mockup for design and fun, not a real
            tweet.
          </p>
        </section>
      </div>
    </div>
  );
}
